import { HStack, IconButton, Text } from '@chakra-ui/react';
import React, { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import {
  QueryFilter,
  useCreditFacilityQueryFilters,
  useCreditFacilityQueryParams,
} from './utils';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { z } from 'zod/v4';

// Types
const RouteParamsStruct = z.object({
  creditFacilityId: z
    .string({ message: 'URL parameter must be a string' })
    .optional(),
});

// Fragments
type FilterPillProps = {
  filterText: string;
  onDeactivateFilter: () => void;
};
const FilterPill = ({ filterText, onDeactivateFilter }: FilterPillProps) => {
  return (
    <HStack
      border="1px"
      borderRadius="8px"
      borderColor={colors.aurora[400]}
      padding="4px 16px"
      alignItems="center"
      spacing="8px"
    >
      <Text fontWeight="500">{filterText}</Text>
      <IconButton
        aria-label={`deactivate ${filterText} filter`}
        onClick={onDeactivateFilter}
        variant="ghost"
        size="xs"
        icon={<Icon icon="x" size="12px" />}
        _hover={{ bgColor: 'none' }}
      />
    </HStack>
  );
};

type QueryFilterPillProps = {
  queryFilter: QueryFilter;
  upsertQueryFilter: Function;
  refetchReceivables: () => void;
};
const QueryFilterPill = ({
  queryFilter,
  upsertQueryFilter,
  refetchReceivables,
}: QueryFilterPillProps) => {
  const { filterName } = queryFilter;

  const onDeactivateFilter = useCallback(() => {
    upsertQueryFilter({ queryFilter: { ...queryFilter, isActive: false } });
    refetchReceivables();
  }, [queryFilter, refetchReceivables, upsertQueryFilter]);

  return (
    <FilterPill
      filterText={filterName}
      onDeactivateFilter={onDeactivateFilter}
    />
  );
};

// Exports
type QueryFiltersPanelProps = {
  organizationId: string;
  refetchReceivables: () => void;
};
export const QueryFiltersPanel = ({
  organizationId,
  refetchReceivables,
}: QueryFiltersPanelProps) => {
  const params = useParams();
  const { creditFacilityId } = RouteParamsStruct.parse(params);

  const {
    creditFacilityId: creditFacilityQueryParam,
  } = useCreditFacilityQueryParams();

  const { queryFilters, upsertQueryFilter } = useCreditFacilityQueryFilters({
    organizationId,
    creditFacilityId: creditFacilityQueryParam ?? creditFacilityId ?? null,
  });

  const activeFilters = queryFilters.filter(
    (queryFilter) => queryFilter.isActive,
  );

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <HStack spacing="10px" w="100%" m="40px 0">
      {activeFilters.map((queryFilter) => (
        <QueryFilterPill
          key={queryFilter.id}
          queryFilter={queryFilter}
          upsertQueryFilter={upsertQueryFilter}
          refetchReceivables={refetchReceivables}
        />
      ))}
    </HStack>
  );
};
